import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { AIAgentMessageListDesktop } from './AIAgentMessageListDesktop';
import AIAgentInputBar from './input-bar';
import { transformCopilotMessagesToDisplay } from '@/hooks/ai-agents/utils/messageTransformers';
import { cn } from '@/lib/utils';
import { ChatHeader } from './chat-header';
import { logInfo, LogCategory } from '@/utils/logger';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useUser } from '@/hooks/auth/use-user';
import { useChatManager } from '@/hooks/ai-agents/useChatManager';

interface AIAgentChatInterfaceDesktopProps {
  threadId?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onClose?: () => void;
}

export const AIAgentChatInterfaceDesktop: React.FC<AIAgentChatInterfaceDesktopProps> = ({
  threadId,
  inputValue = '',
  onInputChange,
  onClose
}) => {
  const {
    messages,
    isGenerating: isLoading,
    error,
    submitMessage: sendMessage,
    currentAssistant
  } = useAIAgentChat();
  
  // ✅ INFINITE SCROLL: Get ChatManager directly for infinite scroll support
  const { user } = useUser();
  const chatManager = useChatManager(user, currentAssistant);
  
  // ✅ ACCESS: Get localStorage state for clearing input after send
  const { clearInput } = useLocalStorageState();
  
  // Memoized computed values
  const hasMessages = useMemo(() => messages.length > 0, [messages.length]);
  const errorMessage = useMemo(() => error?.toString(), [error]);
  
  // Memoized callbacks to prevent child re-renders
  const handleSendMessage = useCallback((message: string) => {
    logInfo('Sending message from desktop chat interface', { 
      category: LogCategory.UI,
      data: { messageLength: message.length }
    });
    sendMessage(message);
    
    // ✅ COMPLETE CLEAR: Clear both the controlled input and localStorage
    if (onInputChange) {
      onInputChange('');
    }
    // Clear localStorage input value to prevent stale data
    clearInput();
  }, [sendMessage, onInputChange, clearInput]);

  const inputPlaceholder = useMemo(() => 
    error ? "Error connecting to assistant" : `Message ${currentAssistant?.name || 'Copilot'}...`
  , [error, currentAssistant?.name]);
  
  return (
    <div className="flex flex-col h-full">
      {errorMessage && (
        <div className="flex-none bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/30 p-2 text-center">
          <p className="text-xs text-red-600 dark:text-red-400">
            Error: {errorMessage}
          </p>
        </div>
      )}
      
      {/* Header - desktop specific, no onClose */}
      <div className="flex-none border-b border-border/20">
        <ChatHeader 
          activeAssistant={currentAssistant} 
          variant="embedded"
          isCompact={hasMessages}
          onClose={onClose}
        />
      </div>
      
      {/* Messages area - takes remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {(threadId || hasMessages) && (
          <AIAgentMessageListDesktop
            messages={messages}
            isLoading={isLoading}
            threadId={threadId}
            onLoadOlderMessages={chatManager.loadOlderMessages}
            hasOlderMessages={chatManager.hasOlderMessages}
            isLoadingOlder={chatManager.isLoadingOlder}
          />
        )}
      </div>
      
      {/* Input bar - desktop specific styling */}
      <div className="flex-none border-t border-border/20">
        <AIAgentInputBar
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          disabled={!!error}
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={onInputChange}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

AIAgentChatInterfaceDesktop.displayName = 'AIAgentChatInterfaceDesktop';

export default AIAgentChatInterfaceDesktop; 